/* eslint-disable no-await-in-loop */
/**
 * Shared job-completion polling for `process_queue` jobs (issue #739).
 *
 * Single implementation of "poll process_queue until completed/failed/timeout",
 * consumed by:
 * - `hooks/useQueueJob.ts` (polling fallback when real-time fails)
 * - `features/prompt-debugger/services/promptService.ts` (waitForJobCompletion)
 * - `services/presetTestApi.ts` (testModelPreset)
 * - `features/stories/pages/StoryCreate.tsx` (blast mode)
 *
 * Terminal states are `completed`, `failed`, and `cancelled` (cancelled is
 * treated as a failure). Sequential polling is intentional — each result must
 * be inspected before deciding to poll again.
 */
import { logger } from '@musedrops/shared';
import { supabase } from './supabase';

export type QueueJobState =
  | 'pending'
  | 'processing'
  | 'completed'
  | 'failed'
  | 'cancelled';

export interface QueueJobSnapshot {
  id: number;
  status: QueueJobState;
  output?: Record<string, unknown>;
  last_error?: string;
}

export type WaitForJobResult =
  /** Job reached `completed` */
  | { outcome: 'completed'; job: QueueJobSnapshot }
  /** Job reached `failed` or `cancelled` */
  | { outcome: 'failed'; job: QueueJobSnapshot; error: string }
  /** Job did not reach a terminal state within timeoutMs */
  | { outcome: 'timeout'; error: string }
  /** Poll query failed or job row not found (only when onPollError='fail') */
  | { outcome: 'poll_error'; error: string }
  /** Aborted via options.signal */
  | { outcome: 'aborted'; error: string };

export interface WaitForJobOptions {
  /** Give-up time in ms (default: 2 minutes). Pass Infinity to never time out. */
  timeoutMs?: number;
  /** Delay between polls in ms (default: 1 second). */
  pollIntervalMs?: number;
  /** Abort polling externally (e.g. component unmount). */
  signal?: AbortSignal;
  /** Called with every snapshot, including non-terminal pending/processing. */
  // eslint-disable-next-line no-unused-vars
  onUpdate?: (job: QueueJobSnapshot) => void;
  /**
   * What to do when a poll query errors or the job row is missing:
   * - 'fail' (default): resolve with outcome 'poll_error'
   * - 'continue': log and keep polling (real-time fallback semantics)
   */
  onPollError?: 'fail' | 'continue';
}

/** Abort-aware sleep: resolves early if the signal fires. */
function sleep(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise<void>(resolve => {
    const timer = setTimeout(() => {
      signal?.removeEventListener('abort', onAbort);
      resolve();
    }, ms);
    function onAbort() {
      clearTimeout(timer);
      resolve();
    }
    signal?.addEventListener('abort', onAbort, { once: true });
  });
}

/**
 * Poll a `process_queue` job until it reaches a terminal state, times out,
 * or is aborted. Never rejects for job-level outcomes — inspect the returned
 * discriminated result instead.
 */
export async function waitForJob(
  jobId: number,
  options: WaitForJobOptions = {}
): Promise<WaitForJobResult> {
  const {
    timeoutMs = 2 * 60 * 1000,
    pollIntervalMs = 1000,
    signal,
    onUpdate,
    onPollError = 'fail',
  } = options;

  const startTime = Date.now();

  while (Date.now() - startTime < timeoutMs) {
    if (signal?.aborted) {
      return { outcome: 'aborted', error: 'Job polling aborted' };
    }

    const { data, error } = await supabase
      .from('process_queue')
      .select('id, status, output, last_error')
      .eq('id', jobId)
      .maybeSingle();

    if (signal?.aborted) {
      return { outcome: 'aborted', error: 'Job polling aborted' };
    }

    if (error) {
      if (onPollError === 'fail') {
        return {
          outcome: 'poll_error',
          error: `Failed to poll job status: ${error.message}`,
        };
      }
      logger.error('waitForJob polling error', { error, jobId });
    } else if (!data) {
      if (onPollError === 'fail') {
        return { outcome: 'poll_error', error: `Job ${jobId} not found` };
      }
      logger.warn('waitForJob: job not found', { jobId });
    } else {
      const job = data as QueueJobSnapshot;
      onUpdate?.(job);

      if (job.status === 'completed') {
        return { outcome: 'completed', job };
      }
      if (job.status === 'failed' || job.status === 'cancelled') {
        return {
          outcome: 'failed',
          job,
          error: job.last_error || `Job ${job.status}`,
        };
      }
      // 'pending' / 'processing' — keep polling
    }

    await sleep(pollIntervalMs, signal);
  }

  return {
    outcome: 'timeout',
    error: `Job ${jobId} timed out after ${timeoutMs}ms`,
  };
}
